import { inject, nextTick, onUnmounted } from 'vue'
import { routeLocationKey, routerKey } from 'vue-router'

import { positionOfState } from '@/utils/historyMemory'
import { mapViewKey, readMapView, writeMapView, type RememberedView } from '@/utils/mapMemory'

/**
 * Remember and restore a Leaflet camera per history entry, so back returns the
 * map to where the user left it — the map-shaped sibling of `useScrollRestore`.
 * See `src/utils/historyMemory.ts` for why the position is kept in our own store
 * rather than by vue-router.
 */
export interface MapViewportRestore {
  /**
   * The remembered camera for the current entry, or `undefined` on a fresh push.
   *
   * Read during setup, BEFORE `L.map()` is constructed: passing the camera to the
   * constructor is what keeps the default view from being laid out and painted
   * first. Also read on each refit decision, since a refit would overwrite it.
   */
  remembered: () => RememberedView | undefined
  /** Record where the map settled. Call from `moveend`, never from `move`. */
  save: (view: RememberedView) => void
  /**
   * Re-apply the remembered camera after a confirmed navigation, for the case
   * where no remount happened. Registering more than once replaces the previous
   * registration.
   */
  onNavigate: (apply: (view: RememberedView) => void) => void
}

/** Does nothing, for a component mounted without a router installed. */
const INERT: MapViewportRestore = {
  remembered: () => undefined,
  save: () => {},
  onNavigate: () => {},
}

/**
 * `id` distinguishes several maps within one screen. `'map'` is right for every
 * screen today; none mounts two.
 */
export function useMapViewportRestore(id = 'map'): MapViewportRestore {
  /**
   * `inject` with an explicit default rather than `useRouter()` / `useRoute()`,
   * which inject non-null and would leave this composable holding `undefined`
   * with no warning of its own. `MapView` is mounted bare in its own unit suite —
   * no router, no history — and has to keep working there, so a missing router is
   * a supported state rather than a bug to assert against.
   */
  const router = inject(routerKey, undefined)
  const route = inject(routeLocationKey, undefined)
  if (!router || !route) return INERT

  /**
   * Never cached. Both halves advance under the component during a navigation,
   * which is the whole point — the same reason `useScrollRestore.currentKey()`
   * recomputes.
   */
  /**
   * An arrow const, not a `function` declaration: declarations are hoisted, so TS
   * discards the narrowing the guard above just established and both injections
   * read as possibly-undefined inside the body.
   */
  const currentKey = (): string =>
    mapViewKey(positionOfState(router.options.history.state), id, route.fullPath)

  let stopAfterEach: (() => void) | undefined

  onUnmounted(() => {
    stopAfterEach?.()
    stopAfterEach = undefined
  })

  return {
    remembered: () => readMapView(currentKey()),

    /**
     * There is deliberately no save on unmount. By the time a map unmounts during
     * a push, `history.state.position` and `route.fullPath` have already advanced
     * to the NEW entry, so a key computed there would file the old screen's camera
     * under the new screen's name. `moveend` fires while the entry is still
     * current, and a pan always settles before the tap that navigates away.
     */
    save: (view) => writeMapView(currentKey(), view),

    onNavigate: (apply) => {
      stopAfterEach?.()
      /**
       * `nextTick`, not the `afterEach` body: `RouterView` reuses the component
       * instance when only params change, and the mode watcher that refits the
       * route runs on the pre-flush queue. Applying synchronously here would land
       * before that refit and be overwritten by it.
       */
      stopAfterEach = router.afterEach(() => {
        void nextTick(() => {
          const view = readMapView(currentKey())
          if (view) apply(view)
        })
      })
    },
  }
}
